/**
 * StellarDateRangePicker — reusable date-range picker (2026-02).
 *
 * Trigger shows the range in the global CRM format:
 *   "July 1, 2026 – July 15, 2026"
 * Click → the same react-day-picker month grid used for deadlines in
 * range mode (pick start, then end) + a Done button.
 *
 * Value contract: { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }.
 * onChange({start, end}) fires when a complete range is selected.
 *
 * WebKit rules inherited from StellarDatePicker (Iter 70.4): hand-rolled
 * popover only — NO Radix Popover, NO asChild trigger composition.
 */
import * as React from 'react';
import { ChevronDown, Calendar as CalendarIcon } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { formatTaskDate } from '../lib/dateFormat';
import { cn } from '../lib/utils';

const parseLocal = (s) => {
  if (!s) return undefined;
  const [y, m, d] = s.slice(0, 10).split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};
const toIsoLocal = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export function StellarDateRangePicker({
  value,            // {start, end} — YYYY-MM-DD strings
  onChange,         // ({start, end}) => void
  className = '',
  disabled = false,
  testId,
}) {
  const [open, setOpen] = React.useState(false);
  // Draft range while the user is mid-selection (from picked, to pending).
  const [draft, setDraft] = React.useState(null);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDocPointer = (e) => {
      const root = containerRef.current;
      if (root && root.contains(e.target)) return;
      setOpen(false); setDraft(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); setDraft(null); } };
    document.addEventListener('mousedown', onDocPointer, true);
    document.addEventListener('touchstart', onDocPointer, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer, true);
      document.removeEventListener('touchstart', onDocPointer, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = draft || {
    from: parseLocal(value?.start),
    to: parseLocal(value?.end),
  };

  const handleSelect = (range) => {
    if (!range || !range.from) { setDraft({ from: undefined, to: undefined }); return; }
    if (range.from && range.to) {
      onChange?.({ start: toIsoLocal(range.from), end: toIsoLocal(range.to) });
      setDraft(null);
      setOpen(false);
    } else {
      setDraft(range); // start picked, waiting for end
    }
  };

  const label = value?.start && value?.end
    ? `${formatTaskDate(value.start)} \u2013 ${formatTaskDate(value.end)}`
    : 'Pick a date range';

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        data-testid={testId}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (disabled) return;
          setOpen((o) => {
            const next = !o;
            // Fresh two-click selection every time the panel opens — the
            // previously committed range stays in the field until a new
            // complete range is picked.
            setDraft(next ? { from: undefined, to: undefined } : null);
            return next;
          });
        }}
        style={{ touchAction: 'manipulation' }}
        className={cn(
          'flex items-center justify-between gap-2 rounded-lg',
          'bg-card border border-border/30 text-foreground',
          'hover:border-border/50 focus:ring-1 focus:ring-gold/40 focus:border-gold/40 focus:outline-none',
          'transition-colors h-8 text-[12px] px-2.5',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
      >
        <span className="flex items-center gap-1.5 truncate">
          <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />
          <span className="truncate">{label}</span>
        </span>
        <ChevronDown className="h-3 w-3 text-muted-foreground/40 shrink-0" />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Choose date range"
          className="absolute top-full right-0 mt-1 z-[100] p-0 bg-card border border-border/40 shadow-xl rounded-md"
          style={{ minWidth: 'max-content' }}
          data-testid={testId ? `${testId}-panel` : undefined}
        >
          <div className="px-3 pt-2 text-[10px] uppercase tracking-wide text-muted-foreground">
            {draft?.from && !draft?.to ? 'Now pick the end date' : 'Pick the start date'}
          </div>
          <Calendar
            mode="range"
            numberOfMonths={2}
            selected={selected}
            onSelect={handleSelect}
            defaultMonth={parseLocal(value?.start)}
            initialFocus
          />
        </div>
      )}
    </div>
  );
}
